import { BAG_PHOTO_OUTCOMES, BAG_PHOTO_POLICIES } from '@croissants/domain/vocabulary';
import { z } from 'zod';
import { ImageUrl, Instant, Uuid } from './common';

/**
 * THE ORDER CAMERA (founder decisions of 2026-10-08 and 09).
 *
 * A shop may ask its counter to photograph the bag when it marks an order
 * ready. The photographs are dispute evidence for operations and the house:
 * Internal's order page, the house's order dialog and the counter's own order
 * view read them, and the GUEST never does - no `/g` shape carries any of this.
 *
 * The objects live in the MEDIA bucket under the signed `bag/` prefix, so every
 * `url` here is a short-lived signed address composed at the moment of
 * answering (`mediaAddress`); a row holds only the key. Thirteen months from
 * `takenAt`, a nightly sweep deletes the object and the row.
 */

/** Per shop, OFF by default. Set by operations and by the house, never by the counter. */
export const BagPhotoPolicy = z.enum(BAG_PHOTO_POLICIES);
export type BagPhotoPolicy = z.infer<typeof BagPhotoPolicy>;

/**
 * What the counter said on its Ready tap: TAKEN (photos follow, uploaded in
 * the background), SKIPPED (an OPTIONAL shop's "Passer" - refused on a
 * MANDATORY one), CAMERA_UNAVAILABLE (the camera could not open; never a
 * reason to block the counter).
 */
export const BagPhotoOutcome = z.enum(BAG_PHOTO_OUTCOMES);
export type BagPhotoOutcome = z.infer<typeof BagPhotoOutcome>;

/** How many photographs one order's bag may carry. */
export const BAG_PHOTO_LIMIT = 10;

/** One photograph: the counter's id for it, a signed address, and when it was taken. */
export const BagPhoto = z.object({
  photoId: Uuid,
  url: ImageUrl,
  takenAt: Instant,
});
export type BagPhoto = z.infer<typeof BagPhoto>;

/**
 * An order's bag photographs as Internal and the house read them: what the
 * counter said, and what has arrived. `outcome` null with no photo is an order
 * the counter said nothing about, and the screens draw nothing for it.
 */
export const BagPhotoRecord = z.object({
  outcome: BagPhotoOutcome.nullable(),
  photos: z.array(BagPhoto).max(BAG_PHOTO_LIMIT),
});
export type BagPhotoRecord = z.infer<typeof BagPhotoRecord>;
